/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useMemo, useRef } from "react"
import { useWidget } from "../../../../providers/widget"
import { useFetchWidget } from "../../../../hooks"
import { useExplorer } from "../../providers"
import { useFetchProperties, withBrowsePriceField, usePropertyPins, withPinsFilters, PropertyPin } from "../../data"
import { ExploreWidgetConfig } from "../../config"
import { ExploreProperty } from "../../data"
import { getMarkerBadgeHtml, getPopupHtml, resolveInfowindowIcons, useGoogleMaps, useGoogleMapsLoadFailed } from "../../../../common/map"
import { noDatesLabel } from "../../../../common/property-card/no-dates-label"
import { resolvePropertyLink, withStayParams } from "../../../../common/property-href"
import { usePropertyHrefFeed } from "../../../../common/use-property-href-feed"
import { getProxiedImageUrl } from "../../../../common/image-proxy"
import { useMapSearchControls, MapBoundsBox } from "./useMapSearchControls"
import MapChrome from "./MapChrome"
import { hoverPanLocked } from "./hoverPanLock"
import { beginMapDragGuard } from "./mapDragGuard"
import { useMobileResults } from "../mobileResults/context"
import { boundsApplied, initialSearchBounds } from "./initialView"
import { resolveStacking, stackingCssVars } from "./mapStacking"
import { createGoogleStackingLayer, GoogleStackingLayer, OverlaySpec } from "./googleStacking"
import { resolveAllPins, extraPinsFor } from './allPins'
import { dotPinSpec, pinCardTooltipHtml, PIN_CARD_LEFT_PX } from './googleAllPins'

function debounce(fn: () => void, ms: number) {
  let timer: ReturnType<typeof setTimeout>
  return () => {
    clearTimeout(timer)
    timer = setTimeout(fn, ms)
  }
}

type Overlay = any
type MarkerEntry = { overlay: Overlay; property: ExploreProperty }

// Google renderer for the Explorer map — mirrors the WP plugin's
// explorer-map-google.js (OverlayView price/icon badges, per-marker tooltip,
// 10%-margin fitBounds, search-on-move) and the widget's Leaflet renderer.
export default function GoogleMap() {
  const { options, feedId, widgetId } = useWidget<ExploreWidgetConfig>()
  const { data: widgetData } = useFetchWidget(feedId, widgetId)
  const pdpRoute = widgetData?.feed?.additional_info?.reverseProxy?.routes?.pdp || '/property/[slug]'
  const hrefFeed = usePropertyHrefFeed(widgetData?.feed)
  const { state, registerMapActions } = useExplorer()
  // Mobile results sheet geometry (#856) — the frozen inactive value outside the sheet layout.
  const mobileResults = useMobileResults()
  const platformUuid = options.filter?.platform

  const mapContainerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<any>(null)
  const markersRef = useRef<MarkerEntry[]>([])
  // Overlay the open popup is anchored to — a plain pin or a spider leaf
  // (whose display position is the fan target, not the property's
  // coordinate), so toggle/close work for both (#1203; mirrors LeafletMap).
  const popupAnchorRef = useRef<Overlay | null>(null)
  // Toolkit map-stacking port (#1203): the Google wiring of the cluster /
  // stacked-badge modes. Rebuilt with the markers so its closures never go
  // stale; disposed with the map.
  const stackingRef = useRef<GoogleStackingLayer | null>(null)
  // Toolkit map-all-pins port (#1313): dot overlays for the full filtered set
  // beyond the loaded page (mirrors LeafletMap's extraMarkersRef, #855).
  const extraOverlaysRef = useRef<Overlay[]>([])
  const programmaticPanRef = useRef(false)

  const searchControls = useMapSearchControls()
  // Init effect runs once per map instance — read the live callbacks through a
  // ref (stale-closure fix, #852; mirrors LeafletMap).
  const searchControlsRef = useRef(searchControls)
  searchControlsRef.current = searchControls

  const getBoundsBox = useCallback((): MapBoundsBox | null => {
    const b = mapRef.current?.getBounds()
    if (!b) return null
    return { north: b.getNorthEast().lat(), south: b.getSouthWest().lat(), east: b.getNorthEast().lng(), west: b.getSouthWest().lng() }
  }, [])

  const mapConfig = options.map ?? { provider: 'google', searchOnMove: true, zoomMin: 0, zoomMax: 18, placement: 'right', showPrice: true, mapType: 'roadmap', hideLocalBusiness: false }
  // Component-scope so the marker effect can depend on it — see LeafletMap (#1243 review).
  const showPrice = mapConfig.showPrice && options.priceDisplay.noDates !== 'none'
  // #1188: pin/popup "No dates available" verdict — null = operator opt-out
  // (legacy price pin); blank = the canonical default wording. Same helper as
  // LeafletMap (#1307 inlined the rule to keep the #1203 rewrite's diff
  // clean; #1310 swapped the helper in once that landed).
  const noDates = noDatesLabel(options)
  const googleLoaded = useGoogleMaps(mapConfig.apiKey)
  const googleFailed = useGoogleMapsLoadFailed()
  const stacking = useMemo(() => resolveStacking(options.map?.stacking), [options.map?.stacking])

  const boundsString = state.mapSearchActive && state.mapVisible && state.mapBounds
    ? `${state.mapBounds.west},${state.mapBounds.south},${state.mapBounds.east},${state.mapBounds.north}`
    : undefined

  const filtersWithBounds = useMemo(() => {
    const filters = { ...state.visitorFilters }
    if (boundsString) filters.map_bounds = boundsString
    return withBrowsePriceField(filters, options)
  }, [state.visitorFilters, boundsString, options])

  const { data } = useFetchProperties(feedId, filtersWithBounds, options.filter.limit, platformUuid)
  // Memoized on the query data — same marker-rebuild trap as LeafletMap (#1243).
  const properties = useMemo(() => data?.pages.flatMap((page) => page.result) ?? [], [data])

  // Toolkit map-all-pins port (#1313; Leaflet shipped first in #855): the
  // FULL filtered set as skinny pins, off by default, riding the exact
  // usePropertyPins query LeafletMap calls — never bounds-scoped
  // (withPinsFilters strips map_bounds and sort_by), so search-as-move
  // panning narrows the cards but never the pin set.
  const allPins = useMemo(() => resolveAllPins(options.map?.allPins), [options.map?.allPins])
  const pinsFilters = useMemo(
    () => withPinsFilters(state.visitorFilters, options),
    [state.visitorFilters, options],
  )
  const dated = !!(state.visitorFilters.checkin && state.visitorFilters.checkout)
  const cardsFirstPage = data?.pages?.[0]?.result
  // Dated-stampede rule (#855): dated queries run the heavy availability work
  // server-side and the pins mirror repeats it — serialize, so the dated pins
  // fetch fires only after the card request SUCCEEDED with results. Undated
  // queries fetch immediately (cheap, shared-cacheable).
  const pinsQueryEnabled = allPins.enabled
    && (!dated || (cardsFirstPage !== undefined && cardsFirstPage.length > 0))
  const { data: pinsData } = usePropertyPins(feedId, pinsFilters, platformUuid, { enabled: pinsQueryEnabled })
  // A successful dated response with ZERO cards resolves the pin set as empty
  // without a mirror fetch (an empty grid must not sit beside the previous
  // filter's map). Memoized — the marker effect keys on this reference.
  const pins = useMemo<PropertyPin[]>(() => {
    if (!allPins.enabled) return []
    if (dated && cardsFirstPage !== undefined && cardsFirstPage.length === 0) return []
    return pinsData?.pins ?? []
  }, [allPins.enabled, dated, cardsFirstPage, pinsData])

  // PDP link with the active visitor filters — slug-based like LeafletMap's,
  // shared by the full property popup and the compact all-pins card (#1313).
  // #1714: the base is the shared resolver's link (External Property Pages).
  const buildPdpUrl = useCallback((slug: string | null): string | null => {
    if (!slug) return null
    const link = resolvePropertyLink({ feed: hrefFeed, slug, pdpRoute })
    const f = state.visitorFilters
    const params = new URLSearchParams()
    if (f?.checkin && f?.checkout) { params.set('checkin', f.checkin); params.set('checkout', f.checkout) }
    if (f?.adults) params.set('adults', String(f.adults))
    if (f?.children) params.set('children', String(f.children))
    const guests = (f?.adults ?? 0) + (f?.children ?? 0)
    if (guests > 0) params.set('guests', String(guests))
    return withStayParams(link, params.toString())
  }, [hrefFeed, pdpRoute, state.visitorFilters])

  const closePopup = useCallback(() => {
    popupAnchorRef.current?.close()
    popupAnchorRef.current = null
  }, [])

  // Open the popup anchored to a live overlay; a second click on the same
  // anchor toggles it closed.
  const openPopupOn = useCallback((property: ExploreProperty, overlay: Overlay) => {
    if (popupAnchorRef.current === overlay) { closePopup(); return }
    closePopup()
    const imgScale = Math.min(Math.ceil((typeof window !== 'undefined' ? window.devicePixelRatio : 2) || 1), 2)
    const imageUrl = property.images?.[0] ? getProxiedImageUrl(property.images[0], 300 * imgScale, 200 * imgScale) : ''
    // Card tags (#1819): the popup's type slot shows the card's pills.
    overlay.open(getPopupHtml(property, imageUrl, buildPdpUrl(property.slug) ?? '#', resolveInfowindowIcons(options.map), noDates, options.card?.cardTags))
    popupAnchorRef.current = overlay
  }, [closePopup, buildPdpUrl, options.map.infowindowLocationsIcon, options.map.infowindowTypesIcon, noDates, options.card])

  // Compact click card for an all-pins dot (#1313): a skinny pin carries only
  // title/slug (+ optional price) — title plus a PDP link, no image fetch.
  // Rides the overlay's own tooltip at the card's smaller centering offset;
  // a second click on the same dot toggles it closed (mirrors LeafletMap).
  const openPinCardOn = useCallback((pin: PropertyPin, overlay: Overlay) => {
    if (popupAnchorRef.current === overlay) { closePopup(); return }
    closePopup()
    overlay.open(pinCardTooltipHtml(pin, buildPdpUrl(pin.slug)), PIN_CARD_LEFT_PX)
    popupAnchorRef.current = overlay
  }, [closePopup, buildPdpUrl])

  const openPopupAtIndex = useCallback((index: number) => {
    if (index < 0 || index >= markersRef.current.length) return
    const { overlay, property } = markersRef.current[index]
    openPopupOn(property, overlay)
  }, [openPopupOn])

  // ─── Init map ────────────────────────────────────────────────────
  useEffect(() => {
    if (!googleLoaded || !mapContainerRef.current || mapRef.current) return
    const g = (window as any).google

    let styles: any[] = []
    if (mapConfig.mapStyles) {
      try { const parsed = JSON.parse(mapConfig.mapStyles); if (Array.isArray(parsed)) styles = parsed } catch { /* ignore bad JSON */ }
    } else if (mapConfig.hideLocalBusiness) {
      styles = [{ featureType: 'poi.business', stylers: [{ visibility: 'off' }] }]
    }

    const map = new g.maps.Map(mapContainerRef.current, {
      minZoom: mapConfig.zoomMin,
      maxZoom: mapConfig.zoomMax,
      zoom: mapConfig.zoomMin,
      center: { lat: 39.8283, lng: -98.5795 },
      mapTypeId: mapConfig.mapType ?? 'roadmap',
      styles,
      disableDefaultUI: true,
      streetViewControl: true,
      // Toolkit mobile-map-gestures port (#1046 Wave A): greedy means
      // one-finger pan and two-finger pinch act on the MAP, and Google Maps
      // preventDefaults touch gestures inside the canvas — which is what
      // actually stops iOS page-zoom (touch-action CSS alone does not).
      // DELIBERATE desktop change too: Google's default (auto → cooperative
      // on a scrollable page) scrolled the page on bare wheel and zoomed only
      // with Ctrl; greedy zooms on bare wheel over the map. That aligns the
      // Google provider with Leaflet (wheel-zooms by default) and with the
      // toolkit, which ships greedy on customer sites desktop included.
      gestureHandling: 'greedy',
    })
    mapRef.current = map

    // A remount while search-as-move is active (the mobile results sheet
    // swaps the renderer's parent across its breakpoint, #856) opens on the
    // active bounds instead of the default camera — mirrors LeafletMap.
    const seed = initialSearchBounds(state)
    if (seed) {
      programmaticPanRef.current = true
      const seedBounds = new g.maps.LatLngBounds(new g.maps.LatLng(seed.south, seed.west), new g.maps.LatLng(seed.north, seed.east))
      // Same band inset as fitBounds: on a sheet remount the area must land
      // above the sheet, not under it (codex r2).
      const band = mobileResults.getFramePadding(mapContainerRef.current)
      if (band) map.fitBounds(seedBounds, band)
      else map.fitBounds(seedBounds)
    }

    const handlePositionChange = debounce(() => {
      if (programmaticPanRef.current) { programmaticPanRef.current = false; return }
      const b = mapRef.current?.getBounds()
      if (!b) return
      searchControlsRef.current.onMoveEnd({ north: b.getNorthEast().lat(), south: b.getSouthWest().lat(), east: b.getNorthEast().lng(), west: b.getSouthWest().lng() })
    }, 500)

    // Genuine-user-gesture wiring (#852): dragstart only fires for real drags;
    // wheel/pinch arrive as container DOM events; zoom buttons notify directly.
    // MVC listener handles are retained and removed on teardown.
    const container = mapContainerRef.current
    const notifyGesture = () => {
      // A genuine gesture supersedes any pending programmatic suppression.
      programmaticPanRef.current = false
      searchControlsRef.current.onUserGesture()
    }
    const listeners = [
      map.addListener('zoom_changed', handlePositionChange),
      map.addListener('dragend', handlePositionChange),
      map.addListener('dragstart', notifyGesture),
    ]
    const onWheel = () => notifyGesture()
    const onTouchMove = (e: TouchEvent) => { if (e.touches.length >= 2) notifyGesture() }
    container.addEventListener('wheel', onWheel, { passive: true })
    container.addEventListener('touchmove', onTouchMove, { passive: true })

    return () => {
      listeners.forEach((l) => l?.remove?.())
      container.removeEventListener('wheel', onWheel)
      container.removeEventListener('touchmove', onTouchMove)
      stackingRef.current?.dispose()
      stackingRef.current = null
      mapRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [googleLoaded])

  // ─── 10%-margin fit bounds (matches WP + Leaflet) ────────────────
  const fitBounds = useCallback(() => {
    const g = (window as any).google
    if (!mapRef.current || !g) return
    const entries = markersRef.current
    if (!entries.length) return
    // Always programmatic framing — never a user position change (#858 fresh
    // pass 2; mirrors LeafletMap).
    programmaticPanRef.current = true
    const bounds = new g.maps.LatLngBounds()
    entries.forEach(({ property }) => {
      if (property.location?.lat && property.location?.lng) bounds.extend(new g.maps.LatLng(property.location.lat, property.location.lng))
    })
    const span = bounds.toSpan()
    const latMargin = span.lat() * 0.1
    const lngMargin = span.lng() * 0.1
    const padded = new g.maps.LatLngBounds(
      new g.maps.LatLng(bounds.getSouthWest().lat() - latMargin, bounds.getSouthWest().lng() - lngMargin),
      new g.maps.LatLng(bounds.getNorthEast().lat() + latMargin, bounds.getNorthEast().lng() + lngMargin),
    )
    // Mobile results sheet (#856): inset the fit into the visible band —
    // under the search CTA, above the sheet (toolkit bandPadding,
    // explorer-mobile-sheet.js:686-707). Google takes the same edge-padding
    // shape directly. Null outside the sheet layout → the plain fit.
    const band = mobileResults.getFramePadding(mapContainerRef.current)
    if (band) mapRef.current.fitBounds(padded, band)
    else mapRef.current.fitBounds(padded)
  }, [mobileResults.getFramePadding])

  const focusMarker = useCallback((propertyId: string) => {
    const g = (window as any).google
    if (!mapRef.current || !g) return
    const index = markersRef.current.findIndex(e => e.property.id === propertyId)
    if (index < 0) return
    const { overlay } = markersRef.current[index]
    // Stacking (#1203): a stacked member highlights its stack badge (and its
    // fanned leaf); a pin folded into a cluster bubble has nothing of its own
    // to invert. The layer reports whether it routed the highlight.
    if (!stackingRef.current?.setHighlight(propertyId, true)) overlay.focus()
    // Hover-capable devices keep the map still on card hover (hoverPanLock,
    // #1046); the pin highlight above already happened.
    if (hoverPanLocked()) return
    const pos = overlay.getPosition()
    const b = mapRef.current.getBounds()
    if (!b || !pos) return
    const span = b.toSpan()
    const latMargin = span.lat() * 0.1
    const lngMargin = span.lng() * 0.1
    const inner = new g.maps.LatLngBounds(
      new g.maps.LatLng(b.getSouthWest().lat() + latMargin, b.getSouthWest().lng() + lngMargin),
      new g.maps.LatLng(b.getNorthEast().lat() - latMargin, b.getNorthEast().lng() - lngMargin),
    )
    if (!inner.contains(pos)) { programmaticPanRef.current = true; mapRef.current.panTo(pos) }
  }, [])

  const blurMarker = useCallback((propertyId: string) => {
    const index = markersRef.current.findIndex(e => e.property.id === propertyId)
    if (index < 0) return
    if (!stackingRef.current?.setHighlight(propertyId, false)) markersRef.current[index].overlay.blur()
  }, [])

  useEffect(() => {
    if (!googleLoaded) return
    registerMapActions({ focusMarker, blurMarker, fitBounds })
    return () => registerMapActions(null)
  }, [googleLoaded, focusMarker, blurMarker, fitBounds, registerMapActions])

  // Mobile results sheet (#856): the container swaps between the stacked
  // 400px box and the full-bleed fixed backdrop when the layout switches.
  // Tell Google to re-measure, then re-frame into the visible band (same
  // first-load / search-off gate as the marker effect). Keyed on the layout
  // switch only — see LeafletMap's twin effect.
  useEffect(() => {
    const g = (window as any).google
    if (!googleLoaded || !mapRef.current || !g) return
    g.maps.event.trigger(mapRef.current, 'resize')
    // Arm suppression only for our own fit. Never CLEAR the flag here: on the
    // same mount an initial-bounds seed may have armed it, and its
    // zoom_changed would otherwise read as a visitor move (codex r2).
    if (markersRef.current.length > 0 && !boundsApplied(state)) fitBounds()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [googleLoaded, mobileResults.active])

  // ─── Build markers when properties change ────────────────────────
  useEffect(() => {
    const g = (window as any).google
    if (!mapRef.current || !googleLoaded || !g) return

    // Tear down the previous render: the stacking layer first (its collapse
    // re-adds the badge it hid, which the pin loop below then removes).
    stackingRef.current?.dispose()
    stackingRef.current = null
    markersRef.current.forEach(({ overlay }) => overlay.setMap(null))
    markersRef.current = []
    extraOverlaysRef.current.forEach((overlay) => overlay.setMap(null))
    extraOverlaysRef.current = []
    popupAnchorRef.current = null

    // OverlayView subclass — defined here (needs google.maps at runtime).
    // Also the factory the stacking layer uses for cluster bubbles, stacked
    // badges, and spider leaves (the OverlaySpec extras, #1203).
    class BadgeOverlay extends g.maps.OverlayView {
      // Inherited MVCObject methods, surfaced for the StackOverlay contract
      // (the base is `any` at compile time; `declare` emits nothing).
      declare setMap: (map: unknown) => void
      declare getMap: () => unknown
      position_: any; spec_: OverlaySpec
      cont_: HTMLDivElement | null = null; badge_: HTMLDivElement | null = null; tooltip_: HTMLDivElement | null = null
      constructor(spec: OverlaySpec) {
        super(); this.spec_ = spec; this.position_ = new g.maps.LatLng(spec.lat, spec.lng)
      }
      onAdd() {
        const { html, className, centered, offset, zIndex, onClick } = this.spec_
        const cont = document.createElement('div')
        cont.style.position = 'absolute'
        if (zIndex != null) cont.style.zIndex = String(zIndex)
        const badge = document.createElement('div')
        if (className) badge.className = className
        badge.innerHTML = html
        badge.style.cursor = 'pointer'
        if (centered) badge.style.transform = 'translate(-50%, -50%)'
        badge.addEventListener('click', (e) => { e.stopPropagation(); onClick?.() })
        cont.appendChild(badge)
        this.cont_ = cont; this.badge_ = badge
        // A click on a badge or its popup must not read as a map click — the
        // spiderfy outside-click collapse listens for those (#1203).
        g.maps.OverlayView.preventMapHitsFrom?.(cont)
        this.getPanes().overlayMouseTarget.appendChild(cont)
        // Spider leaf: slide from the anchor out to its fan position (the
        // toolkit animation; .hfse-spider-animating carries the transition).
        if (offset) {
          cont.style.transform = 'translate(0px, 0px)'
          requestAnimationFrame(() => {
            cont.classList.add('hfse-spider-animating')
            cont.style.transform = `translate(${offset.x}px, ${offset.y}px)`
            setTimeout(() => cont.classList.remove('hfse-spider-animating'), 350)
          })
        }
      }
      draw() {
        if (!this.cont_) return
        const p = this.getProjection().fromLatLngToDivPixel(this.position_)
        if (p) { this.cont_.style.left = `${p.x}px`; this.cont_.style.top = `${p.y}px` }
      }
      onRemove() { if (this.cont_?.parentNode) { this.cont_.parentNode.removeChild(this.cont_); this.cont_ = null } }
      open(html: string, leftPx = -150) {
        if (!this.cont_) return
        if (!this.tooltip_) {
          const t = document.createElement('div')
          t.style.position = 'absolute'; t.style.top = `${(this.badge_?.offsetHeight ?? 40)}px`; t.style.zIndex = '1000002'
          this.cont_.appendChild(t); this.tooltip_ = t
        }
        this.tooltip_.style.left = `${leftPx}px`
        this.tooltip_.innerHTML = html
        this.tooltip_.style.display = 'block'
        const closeBtn = this.tooltip_.querySelector('.hfse-map-tooltip-close-btn')
        if (closeBtn) closeBtn.addEventListener('click', (e) => { e.stopPropagation(); this.close() })
      }
      close() { if (this.tooltip_) this.tooltip_.style.display = 'none' }
      focus() { this.badge_?.classList.add(this.spec_.focusClass ?? 'hfse-map-badge-focus') }
      blur() { this.badge_?.classList.remove(this.spec_.focusClass ?? 'hfse-map-badge-focus') }
      setOpacity(opacity: number | null) { if (this.cont_) this.cont_.style.opacity = opacity == null ? '' : String(opacity) }
      isOnMap() { return !!this.getMap() }
      getPosition() { return this.position_ }
    }

    properties.forEach((property) => {
      if (!property.location?.lat || !property.location?.lng) return
      const overlay = new BadgeOverlay({
        lat: property.location.lat,
        lng: property.location.lng,
        html: getMarkerBadgeHtml(property, showPrice, noDates),
        onClick: () => {
          const idx = markersRef.current.findIndex(e => e.property.id === property.id)
          if (idx >= 0) openPopupAtIndex(idx)
        },
      })
      // With stacking on, the layer below decides which pins show (#1203).
      if (!stacking.enabled) overlay.setMap(mapRef.current)
      markersRef.current.push({ overlay, property })
    })

    // Toolkit map-stacking port (#1203): cluster-primary folds nearby pins
    // into tinted count bubbles that split on zoom-in and spiderfy at max
    // zoom; stacked-badge mode collapses only exactly co-located pins into a
    // price-range badge that fans out on click. The pure halves and the
    // spiderfy state machine are shared with LeafletMap; googleStacking.ts
    // owns the Google wiring.
    if (stacking.enabled) {
      const layer = createGoogleStackingLayer<ExploreProperty>({
        g,
        map: mapRef.current,
        stacking,
        maxZoom: mapConfig.zoomMax,
        showPrice,
        entries: markersRef.current,
        createOverlay: (spec) => new BadgeOverlay(spec),
        badgeHtml: (property) => getMarkerBadgeHtml(property, showPrice, noDates),
        onLeafClick: openPopupOn,
        onFanTeardown: closePopup,
      })
      stackingRef.current = layer
      layer.refresh()
    }

    // All-pins extras (#1313): every home in the filtered set beyond the
    // loaded page as a lightweight dot under the price badges. Dots stay
    // OUTSIDE the cluster/stack groups — the stacking layer partitions the
    // property pins only. As pages load in, matching dots drop out (the
    // dedupe against renderedIds re-runs with every rebuild, #855).
    if (pins.length) {
      const renderedIds = new Set(properties.map((p) => p.id))
      extraPinsFor(pins, renderedIds).forEach((pin) => {
        const dot: Overlay = new BadgeOverlay(dotPinSpec(pin, () => openPinCardOn(pin, dot)))
        dot.setMap(mapRef.current)
        extraOverlaysRef.current.push(dot)
      })
    }

    if (markersRef.current.length > 0 && !boundsApplied(state)) fitBounds()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [properties, pins, googleLoaded, fitBounds, openPopupAtIndex, openPopupOn, openPinCardOn, closePopup, showPrice, noDates, stacking, mapConfig.zoomMax])

  const handleSearchToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    searchControls.onToggle(e.target.checked, getBoundsBox())
  }

  // Zoom buttons are genuine gestures: clear pending programmatic suppression
  // first so their own idle/moveend is not discarded (#858 round 4).
  const handleZoomIn = () => { programmaticPanRef.current = false; searchControls.onUserGesture(); mapRef.current?.setZoom((mapRef.current?.getZoom() ?? 0) + 1) }
  const handleZoomOut = () => { programmaticPanRef.current = false; searchControls.onUserGesture(); mapRef.current?.setZoom((mapRef.current?.getZoom() ?? 0) - 1) }
  const handleFullscreen = () => {
    const el = mapContainerRef.current?.parentElement
    if (!el) return
    if (document.fullscreenElement) document.exitFullscreen(); else el.requestFullscreen()
  }

  return (
    <div className="hfse-map-container" style={stacking.enabled ? (stackingCssVars(stacking) as React.CSSProperties) : undefined}>
      <div className="hfse-map" ref={mapContainerRef} style={{ width: '100%', height: '100%' }} onPointerDown={(e) => beginMapDragGuard(e.currentTarget)}>
        {!googleLoaded && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            {!mapConfig.apiKey
              ? 'Google Maps API key required.'
              : googleFailed
                ? 'Map could not be loaded.'
                : 'Loading map…'}
          </div>
        )}
      </div>

      {googleLoaded && (
        <div className="hfse-map-controls" style={{ ...(options.map.controlBg ? { '--hfse-map-control-bg': options.map.controlBg } : {}), ...(options.map.controlColor ? { '--hfse-map-control-color': options.map.controlColor } : {}) } as React.CSSProperties}>
          <MapChrome
            onZoomIn={handleZoomIn}
            onZoomOut={handleZoomOut}
            onFullscreen={handleFullscreen}
            showFullscreen={options.map?.showFullscreen !== false}
          />
          {searchControls.searchOnMove && !searchControls.concealSearchToggle && (
            <span className="map-control control-search">
              <div className="map-control-input-container">
                <input type="checkbox" id="hr-map-search-checkbox-google" className="map-control-checkbox" checked={searchControls.searchActive} onChange={handleSearchToggle} />
                <label htmlFor="hr-map-search-checkbox-google" className="map-control-label">Search as I move the map</label>
              </div>
            </span>
          )}
          {searchControls.showSearchArea && (
            <button type="button" className="hfse-map-search-area-btn" onClick={() => searchControls.onSearchAreaClick(getBoundsBox())}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
                <circle cx="11" cy="11" r="7" />
                <path d="m21 21-4.35-4.35" />
              </svg>
              Search this area
            </button>
          )}
        </div>
      )}
    </div>
  )
}
